import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { BadgeCheck, Check, Gift, Loader, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/salon/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';

const API = '/api';

const BillingPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [billingData, setBillingData] = useState(null);
  const [interval, setInterval] = useState('month');
  const [loading, setLoading] = useState('');
  const [gift, setGift] = useState({ amount: 50, recipient_name: '', recipient_email: '', personal_message: '' });

  useEffect(() => {
    axios.get(`${API}/billing/plans`)
      .then((response) => setPlans(response.data))
      .catch(() => toast.error('Angebote konnten nicht geladen werden'));
    if (new URLSearchParams(location.search).get('cancelled')) {
      toast.info('Zahlung abgebrochen – es wurde nichts berechnet.');
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setBillingData(null);
      return;
    }
    axios.get(`${API}/billing/me`, { headers: getAuthHeaders() })
      .then((response) => setBillingData(response.data))
      .catch(() => setBillingData(null));
  }, [user?.id]);

  const activeMembership = billingData?.membership;
  const activeGiftCards = useMemo(
    () => (billingData?.gift_cards || []).filter((card) => card.status === 'active'),
    [billingData]
  );

  const requireLogin = () => {
    if (user) return true;
    navigate('/login?redirect=angebote');
    return false;
  };

  const startMembership = async (planId) => {
    if (!requireLogin()) return;
    setLoading(`membership-${planId}`);
    try {
      const response = await axios.post(
        `${API}/billing/subscriptions/checkout`,
        { plan_id: planId, billing_interval: interval },
        { headers: getAuthHeaders() }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Abo konnte nicht gestartet werden');
      setLoading('');
    }
  };

  const buyGiftCard = async (event) => {
    event.preventDefault();
    if (!requireLogin()) return;
    setLoading('gift');
    try {
      const response = await axios.post(
        `${API}/billing/gift-cards/checkout`,
        { ...gift, amount: Number(gift.amount) },
        { headers: getAuthHeaders() }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gutschein konnte nicht erstellt werden');
      setLoading('');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main>
        <section className="bg-gradient-to-br from-slate-950 via-blue-950 to-blue-700 text-white">
          <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20 text-center">
            <Sparkles className="w-10 h-10 mx-auto mb-4 text-blue-200" />
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Salon Galo Clubs & Gutscheine</h1>
            <p className="mt-4 text-blue-100 max-w-2xl mx-auto">
              Regelmäßig frisch aussehen oder Freude verschenken – sicher online bezahlt.
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-12">
          {activeMembership && (
            <Card className="mb-10 p-6 border-emerald-200 bg-emerald-50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex gap-3">
                  <BadgeCheck className="w-7 h-7 text-emerald-600 flex-shrink-0" />
                  <div>
                    <h2 className="font-bold text-slate-900">{activeMembership.plan?.name} ist aktiv</h2>
                    <p className="text-sm text-slate-600">
                      Noch {activeMembership.remaining_visits} von {activeMembership.plan?.included_visits} Terminen in diesem Monat verfügbar.
                    </p>
                  </div>
                </div>
                <Button onClick={() => navigate('/?booking=open')} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                  Termin mit Abo buchen
                </Button>
              </div>
            </Card>
          )}

          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white border rounded-full p-1 shadow-sm">
              <button onClick={() => setInterval('month')} className={`px-5 py-2 rounded-full text-sm font-semibold ${
                interval === 'month' ? 'bg-blue-600 text-white' : 'text-slate-600'
              }`}>Monatlich</button>
              <button onClick={() => setInterval('year')} className={`px-5 py-2 rounded-full text-sm font-semibold ${
                interval === 'year' ? 'bg-blue-600 text-white' : 'text-slate-600'
              }`}>Jährlich sparen</button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {plans.map((plan) => {
              const price = interval === 'month' ? plan.monthly_amount : plan.yearly_amount;
              return (
                <Card key={plan.id} className="p-7 relative overflow-hidden border-slate-200 hover:shadow-xl transition-shadow">
                  {plan.includes_beard && (
                    <span className="absolute right-0 top-0 bg-blue-600 text-white text-xs font-bold px-4 py-2 rounded-bl-xl">BELIEBT</span>
                  )}
                  <h2 className="text-2xl font-bold text-slate-900">{plan.name}</h2>
                  <p className="text-slate-600 mt-2 min-h-[48px]">{plan.description}</p>
                  <div className="my-6">
                    <span className="text-4xl font-bold text-slate-900">{Number(price).toFixed(2).replace('.', ',')} €</span>
                    <span className="text-slate-500"> / {interval === 'month' ? 'Monat' : 'Jahr'}</span>
                  </div>
                  <ul className="space-y-3 text-sm text-slate-700 mb-7">
                    <li className="flex gap-2"><Check className="w-5 h-5 text-emerald-600" /> {plan.included_visits} Haarschnitte pro Monat</li>
                    <li className="flex gap-2"><Check className="w-5 h-5 text-emerald-600" /> Online direkt als Abo-Termin buchbar</li>
                    {plan.includes_beard && <li className="flex gap-2"><Check className="w-5 h-5 text-emerald-600" /> Bartservice inklusive</li>}
                  </ul>
                  <Button
                    onClick={() => startMembership(plan.id)}
                    disabled={Boolean(activeMembership) || loading === `membership-${plan.id}`}
                    className="w-full rounded-full bg-blue-600 hover:bg-blue-700"
                  >
                    {loading === `membership-${plan.id}` ? <Loader className="w-4 h-4 animate-spin" /> : activeMembership ? 'Abo bereits aktiv' : 'Abo auswählen'}
                  </Button>
                </Card>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Gift className="w-8 h-8 text-blue-600" />
                <h2 className="text-3xl font-bold text-slate-900">Digitaler Gutschein</h2>
              </div>
              <p className="text-slate-600 mb-5">
                Nach erfolgreicher Zahlung wird der Gutscheincode direkt per E-Mail versendet.
              </p>
              <div className="space-y-3 text-sm text-slate-700">
                <p className="flex gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" /> Sichere Zahlung über Stripe</p>
                <p className="flex gap-2"><Check className="w-5 h-5 text-emerald-600" /> Wert frei zwischen 10 € und 500 € wählbar</p>
              </div>
              {activeGiftCards.length > 0 && (
                <div className="mt-7 space-y-3">
                  <h3 className="font-semibold text-slate-900">Meine aktiven Gutscheine</h3>
                  {activeGiftCards.map((card) => (
                    <div key={card.id} className="bg-white border rounded-xl p-4 flex justify-between gap-3">
                      <div><p className="font-bold text-blue-700">{card.code}</p><p className="text-xs text-slate-500">für {card.recipient_name}</p></div>
                      <strong>{Number(card.balance).toFixed(2).replace('.', ',')} €</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Card className="p-6 sm:p-8">
              <form onSubmit={buyGiftCard} className="space-y-5">
                <div>
                  <Label htmlFor="gift-amount">Gutscheinwert in Euro</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2 mb-2">
                    {[25, 50, 100].map((amount) => (
                      <button key={amount} type="button" onClick={() => setGift({ ...gift, amount })}
                        className={`py-2 rounded-lg border font-semibold ${Number(gift.amount) === amount ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200'}`}>
                        {amount} €
                      </button>
                    ))}
                  </div>
                  <Input id="gift-amount" type="number" min="10" max="500" step="1" value={gift.amount}
                    onChange={(e) => setGift({ ...gift, amount: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="recipient-name">Name des Empfängers</Label>
                  <Input id="recipient-name" className="mt-2" value={gift.recipient_name}
                    onChange={(e) => setGift({ ...gift, recipient_name: e.target.value })} placeholder="Max Mustermann" />
                </div>
                <div>
                  <Label htmlFor="recipient-email">E-Mail des Empfängers</Label>
                  <Input id="recipient-email" type="email" className="mt-2" value={gift.recipient_email}
                    onChange={(e) => setGift({ ...gift, recipient_email: e.target.value })} placeholder={user?.email || 'name@email.de'} />
                </div>
                <div>
                  <Label htmlFor="gift-message">Persönliche Nachricht (optional)</Label>
                  <Textarea id="gift-message" className="mt-2" maxLength={500} value={gift.personal_message}
                    onChange={(e) => setGift({ ...gift, personal_message: e.target.value })} placeholder="Viel Freude mit deinem Gutschein!" />
                </div>
                <Button type="submit" disabled={loading === 'gift'} className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
                  {loading === 'gift' ? <Loader className="w-4 h-4 animate-spin" /> : 'Gutschein sicher kaufen'}
                </Button>
              </form>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
};

export default BillingPage;
